import { Box, Typography, alpha, styled } from '@mui/material';
import { OndemandVideoTwoTone } from '@mui/icons-material';

/* ─── Styled ─── */

const VideoFrame = styled(Box)(
  ({ theme }) => `
    position: relative;
    width: 100%;
    padding-top: 56.25%; /* 16:9 */
    border-radius: ${theme.general.borderRadiusLg};
    overflow: hidden;
    background: #000;
    box-shadow: 0 8px 24px ${alpha(theme.colors.alpha.black[100], 0.18)};

    & > iframe,
    & > video {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border: 0;
    }
  `
);

const Placeholder = styled(Box)(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    width: 100%;
    padding-top: 56.25%; /* 16:9 */
    position: relative;
    border-radius: ${theme.general.borderRadiusLg};
    border: 2px dashed ${theme.colors.alpha.black[30]};
    background: ${alpha(theme.colors.primary.main, 0.03)};

    & > .placeholder-inner {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: ${theme.spacing(1)};
      color: ${theme.colors.alpha.black[50]};
    }
  `
);

interface VideoBlockProps {
  /** Yerel dosya yolu (/static/videos/x.mp4) veya YouTube/Vimeo linki. */
  videoUrl?: string;
  /** Erişilebilirlik/başlık için makale adı. */
  title?: string;
}

/** YouTube linkini gömülebilir (embed) URL'ye çevirir. Desteklenmiyorsa null döner. */
function toYouTubeEmbed(url: string): string | null {
  // https://youtu.be/ID
  const short = url.match(/youtu\.be\/([\w-]{6,})/);
  if (short) return `https://www.youtube.com/embed/${short[1]}`;
  // https://www.youtube.com/watch?v=ID
  const watch = url.match(/[?&]v=([\w-]{6,})/);
  if (watch) return `https://www.youtube.com/embed/${watch[1]}`;
  // https://www.youtube.com/embed/ID (zaten embed)
  if (/youtube\.com\/embed\//.test(url)) return url;
  return null;
}

/** Vimeo linkini gömülebilir URL'ye çevirir. Desteklenmiyorsa null döner. */
function toVimeoEmbed(url: string): string | null {
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (m) return `https://player.vimeo.com/video/${m[1]}`;
  if (/player\.vimeo\.com\/video\//.test(url)) return url;
  return null;
}

function VideoBlock({ videoUrl, title }: VideoBlockProps) {
  const url = videoUrl?.trim();

  // Boş → yer tutucu
  if (!url) {
    return (
      <Placeholder>
        <Box className="placeholder-inner">
          <OndemandVideoTwoTone sx={{ fontSize: 48, opacity: 0.5 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Video yakında eklenecek
          </Typography>
          <Typography variant="caption" sx={{ textTransform: 'none' }}>
            Bu anlatım için eğitim videosu hazırlanıyor.
          </Typography>
        </Box>
      </Placeholder>
    );
  }

  // Yerel dosya (mp4/webm/ogg) → <video>
  if (/\.(mp4|webm|ogg)(\?.*)?$/i.test(url)) {
    return (
      <VideoFrame>
        <video controls preload="metadata" controlsList="nodownload">
          <source src={url} />
          Tarayıcınız video oynatmayı desteklemiyor.
        </video>
      </VideoFrame>
    );
  }

  // YouTube / Vimeo → <iframe>
  const embed = toYouTubeEmbed(url) || toVimeoEmbed(url);
  if (embed) {
    return (
      <VideoFrame>
        <iframe
          src={embed}
          title={title || 'Eğitim videosu'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </VideoFrame>
    );
  }

  // Tanınmayan link → yine de <video> olarak dene (genel bağlantı)
  return (
    <VideoFrame>
      <video controls preload="metadata">
        <source src={url} />
        Tarayıcınız video oynatmayı desteklemiyor.
      </video>
    </VideoFrame>
  );
}

export default VideoBlock;
